// SelectionDetails.tsx
import { FC, memo } from "react";
import { SelectionDetailsProps } from "@/types/BigDataMap.types";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "../ui/scroll-area";

// Create a comparison function for React.memo
const areEqual = (prevProps: SelectionDetailsProps, nextProps: SelectionDetailsProps) => {
  return (
    prevProps.selectionMode === nextProps.selectionMode &&
    prevProps.selectedFeatureId === nextProps.selectedFeatureId &&
    prevProps.selectedCluster === nextProps.selectedCluster &&
    // Only compare selectedAreaCounts if they exist and have changed
    ((!prevProps.selectedAreaCounts && !nextProps.selectedAreaCounts) ||
      JSON.stringify(prevProps.selectedAreaCounts) === JSON.stringify(nextProps.selectedAreaCounts)) &&
    // Only compare selectedClusterStats if they exist and have changed
    ((!prevProps.selectedClusterStats && !nextProps.selectedClusterStats) ||
      JSON.stringify(prevProps.selectedClusterStats) === JSON.stringify(nextProps.selectedClusterStats))
  );
};

const SelectionDetails: FC<SelectionDetailsProps> = ({
  selectionMode,
  selectedFeatureId,
  selectedAreaCounts,
  clusterColors,
  combinedGeoJSON,
  selectedCluster,
  selectedClusterStats,
}) => {
  if (!selectedFeatureId && selectedCluster === null) return null;

  const getFeatureDetails = () => {
    if (!selectedFeatureId || !combinedGeoJSON) return null;

    // Split only once in case ID contains hyphens
    const [type, ...idParts] = selectedFeatureId.split("-");
    const idString = idParts.join("-"); // Rejoin the rest in case of multiple hyphens

    // Remove the redundant type prefix if it exists
    const cleanIdString = idString.replace(`${type}-`, "");

    //console.log("Looking for feature:", { type, cleanIdString });

    const feature = combinedGeoJSON.features.find((f) => {
      const featureType = f.properties?.type;
      const featureId = String(f.id);
      const matches = featureType === type && featureId === cleanIdString;

      return matches;
    });

    if (!feature) {
      //console.warn("No matching feature found for:", selectedFeatureId);
      return null;
    }

    return {
      name: feature.properties?.name || "",
      type: type.charAt(0).toUpperCase() + type.slice(1),
    };
  };
  // Region Mode Display
  if (selectionMode === "region" && selectedFeatureId && selectedAreaCounts) {
    const featureDetails = getFeatureDetails();
    //console.log(featureDetails);
    if (!featureDetails) return null;

    return (
      <Card className="absolute top-4 right-4 pt-4 shadow-lg w-64">
        <CardContent className="max-h-[250px]">
          <h3 className="text-lg font-extrabold">
            {featureDetails.type}: {featureDetails.name}
          </h3>

          <div className="space-y-2">
            {/* Total Points */}
            <div>
              <div className="text-base font-semibold ">
                Total Points: <span className="text-base font-light ">{selectedAreaCounts.totalPoints}</span>
              </div>
            </div>

            {/* Cluster Distribution */}
            <div>
              <ScrollArea className="h-[150px]" type="always">
                <p className="text-xs text-muted-foreground mb-2">Cluster Distribution:</p>
                <div className="space-y-2">
                  {Object.entries(selectedAreaCounts)
                    .filter(([key]) => key !== "totalPoints")
                    .sort((a, b) => Number(b[1]) - Number(a[1])) // Sort by count descending
                    .map(([cluster, count]) => {
                      const color = clusterColors[Number(cluster)];
                      const percentage = ((count / selectedAreaCounts.totalPoints) * 100).toFixed(1);

                      return (
                        <div key={cluster} className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-sm align-top flex-shrink-0"
                            style={{
                              backgroundColor: `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3] / 255})`,
                            }}
                          />
                          <div className="flex-1 text-xs">
                            <span className="font-xs">Cluster {cluster}</span>
                            <br />
                            <span className="text-muted-foreground">
                              {count} points ({percentage}%)
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </ScrollArea>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Cluster Mode Display
  if (selectionMode === "cluster" && selectedCluster !== null && selectedClusterStats) {
    const clusterColor = clusterColors[selectedCluster];

    return (
      <Card className="absolute top-2 right-2 p-2 shadow-lg w-64 h-max">
        <div className="flex items-center gap-2 ">
          <div
            className="w-4 h-4 rounded-sm flex-shrink-0"
            style={{
              backgroundColor: `rgba(${clusterColor[0]}, ${clusterColor[1]}, ${clusterColor[2]}, ${clusterColor[3] / 255})`,
            }}
          />
          <h3 className="text-lg font-semibold">Cluster {selectedCluster}</h3>
        </div>
        <CardContent className="w-full">
          <div className="">
            
            <ScrollArea className="h-[150px]" type="always">
              {/* Rest of the component remains the same */}
            {/* Total Points */}
            <div>
              <p className="text-base text-muted-foreground">Total Points: {selectedClusterStats.pointCount}</p>
            </div>
              {/* Statistics */}
              <div className="pb-2">
                {selectedClusterStats.avgR2 !== null && (
                  <div>
                    <span className="text-sm text-muted-foreground">Average R²:</span>{" "}
                    <span className="text-sm font-light">{selectedClusterStats.avgR2.toFixed(3)}</span>
                  </div>
                )}
                {selectedClusterStats.avgMSE !== null && (
                  <div>
                    <span className="text-sm text-muted-foreground">Average MSE:</span>{" "}
                    <span className="text-sm  font-light">{selectedClusterStats.avgMSE.toFixed(3)}</span>
                  </div>
                )}
              </div>

              {/* Region Distribution */}
              {selectedClusterStats.regionCounts &&
                Object.entries(selectedClusterStats.regionCounts).some(([_, count]) => count > 0) && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2 font-semibold">Region Distribution</p>
                    <div className=" max-h-48 overflow-y-auto">
                      {Object.entries(selectedClusterStats.regionCounts)
                        .filter(([_, count]) => count > 0)
                        .sort((a, b) => b[1] - a[1])
                        .map(([region, count]) => (
                          <div key={region} className="text-xs font-light">
                            <span className="font-light">{region}</span>
                            <span className="text-muted-foreground"> ({count} points)</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
            </ScrollArea>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default memo(SelectionDetails, areEqual);
